import { type CorpusResponse, type KnowledgeCorpusEntityState } from 'shared/api/copilot'
import { type CirrusSourceOption, isCirrusCollection, isCirrusStandardReportKind } from 'shared/copilot/cirrusSource'
import { type CopilotSurface } from 'shared/copilot/copilotSurfaces'
import { type FlowDatasourceEntity, type FlowDatasourceEntityType } from 'shared/copilot/flowDataAgentConstants'
import { get as getFromLocalStore, LOCAL_STORAGE_KEYS, store as saveToLocalStore } from 'shared/lstore'

/** Ids + label only — full CorpusResponse (emails, syncStatus, …) is rebuilt from live lists. */
export interface PersistedKnowledgeSource {
  id: string
  name: string
}

/** Ids + type + name only — Flow `data` is rebuilt from live account options. */
export interface PersistedDatasourceEntity {
  id: string
  type: FlowDatasourceEntityType
  name: string
}

export type PersistedDatasourceSelection =
  | {
      kind: 'entities'
      optionIds: string[]
      entities: PersistedDatasourceEntity[]
    }
  | {
      kind: 'cirrus'
      option: CirrusSourceOption
    }
  | {
      kind: 'none'
    }

export interface PersistedSourceSelection {
  v: 1
  datasource: PersistedDatasourceSelection
  knowledgeSourceIds: string[]
  knowledgeSources: PersistedKnowledgeSource[]
}

export const EMPTY_SOURCE_SELECTION: PersistedSourceSelection = {
  v: 1,
  datasource: { kind: 'none' },
  knowledgeSourceIds: [],
  knowledgeSources: []
}

const FLOW_DATASOURCE_ENTITY_TYPES: ReadonlySet<string> = new Set([
  'google_ads_account',
  'ga4_property',
  'gsc_property',
  'meta_ads_account',
  'linkedin_ads_account',
  'tiktok_ads_account',
  'snapchat_ads_account',
  'pinterest_ads_account'
])

const persistJson = (key: string, value: unknown): void => {
  try {
    saveToLocalStore(key, JSON.stringify(value))
  } catch {
    // Quota / private mode — selection still works in memory.
  }
}

const loadJson = (key: string): unknown => {
  try {
    const stored = getFromLocalStore(key)
    if (stored == null) {
      return null
    }
    return JSON.parse(stored) as unknown
  } catch {
    return null
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)

const isFlowDatasourceEntityType = (value: unknown): value is FlowDatasourceEntityType =>
  typeof value === 'string' && FLOW_DATASOURCE_ENTITY_TYPES.has(value)

const isPersistedDatasourceEntity = (value: unknown): value is PersistedDatasourceEntity => {
  if (!isRecord(value)) return false
  return typeof value.id === 'string' && isFlowDatasourceEntityType(value.type) && typeof value.name === 'string'
}

const isCirrusSourceOption = (value: unknown): value is CirrusSourceOption => {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'string' || typeof value.label !== 'string') return false
  if (typeof value.collection !== 'string' || !isCirrusCollection(value.collection)) return false
  if (value.configId != null && typeof value.configId !== 'string') return false
  if (value.kind != null && (typeof value.kind !== 'string' || !isCirrusStandardReportKind(value.kind))) {
    return false
  }
  return true
}

const isPersistedKnowledgeSource = (value: unknown): value is PersistedKnowledgeSource => {
  if (!isRecord(value)) return false
  return typeof value.id === 'string' && typeof value.name === 'string'
}

const parseDatasource = (value: unknown): PersistedDatasourceSelection => {
  if (!isRecord(value) || typeof value.kind !== 'string') {
    return { kind: 'none' }
  }
  if (value.kind === 'none') {
    return { kind: 'none' }
  }
  if (value.kind === 'entities') {
    const optionIds = Array.isArray(value.optionIds)
      ? value.optionIds.filter((id): id is string => typeof id === 'string')
      : []
    const entities = Array.isArray(value.entities) ? value.entities.filter(isPersistedDatasourceEntity) : []
    if (optionIds.length === 0 || entities.length === 0) {
      return { kind: 'none' }
    }
    // Drop any legacy `data` blob — only id/type/name are kept for restore.
    return {
      kind: 'entities',
      optionIds,
      entities: entities.map(({ id, type, name }) => ({ id, type, name }))
    }
  }
  if (value.kind === 'cirrus' && isCirrusSourceOption(value.option)) {
    return { kind: 'cirrus', option: value.option }
  }
  return { kind: 'none' }
}

export const parsePersistedSourceSelection = (value: unknown): PersistedSourceSelection | null => {
  if (!isRecord(value) || value.v !== 1) {
    return null
  }
  const knowledgeSources = Array.isArray(value.knowledgeSources)
    ? value.knowledgeSources.filter(isPersistedKnowledgeSource).map(({ id, name }) => ({ id, name }))
    : []
  const knowledgeSourceIds = Array.isArray(value.knowledgeSourceIds)
    ? value.knowledgeSourceIds.filter((id): id is string => typeof id === 'string')
    : knowledgeSources.map((source) => source.id)

  return {
    v: 1,
    datasource: parseDatasource(value.datasource),
    knowledgeSourceIds,
    knowledgeSources
  }
}

/** Pre-surface-split keys — only fullscreen (Alvie AI Pro) reads these as a fallback once. */
const getLegacyChatSourceSelectionStorageKey = (clientId: number, chatId: string): string =>
  `copilotSourceSelection:v1:${clientId}:chat:${chatId}`

const getLegacyLastSourceSelectionStorageKey = (clientId: number): string => `copilotLastSourceSelection:v1:${clientId}`

export const getChatSourceSelectionStorageKey = (clientId: number, chatId: string, surface: CopilotSurface): string =>
  LOCAL_STORAGE_KEYS.COPILOT_SOURCE_SELECTION(clientId, chatId, surface)

export const getLastSourceSelectionStorageKey = (clientId: number, surface: CopilotSurface): string =>
  LOCAL_STORAGE_KEYS.COPILOT_LAST_SOURCE_SELECTION(clientId, surface)

const readChatSourceSelectionFromKey = (key: string): PersistedSourceSelection | null =>
  parsePersistedSourceSelection(loadJson(key))

export const readChatSourceSelection = (
  clientId: number,
  chatId: string,
  surface: CopilotSurface
): PersistedSourceSelection | null => {
  const scoped = readChatSourceSelectionFromKey(getChatSourceSelectionStorageKey(clientId, chatId, surface))
  if (scoped != null || surface !== 'fullscreen') {
    return scoped
  }
  return readChatSourceSelectionFromKey(getLegacyChatSourceSelectionStorageKey(clientId, chatId))
}

/** Snapshot taken at send time — restored when this chat is opened again. */
export const writeChatSourceSelection = (
  clientId: number,
  chatId: string,
  surface: CopilotSurface,
  selection: PersistedSourceSelection
): void => {
  persistJson(getChatSourceSelectionStorageKey(clientId, chatId, surface), selection)
}

export const readLastSourceSelection = (clientId: number, surface: CopilotSurface): PersistedSourceSelection | null => {
  const scoped = parsePersistedSourceSelection(loadJson(getLastSourceSelectionStorageKey(clientId, surface)))
  if (scoped != null || surface !== 'fullscreen') {
    return scoped
  }
  return parsePersistedSourceSelection(loadJson(getLegacyLastSourceSelectionStorageKey(clientId)))
}

/** Per-surface last-used — updated on every picker click; seeds Create new on that surface. */
export const writeLastSourceSelection = (
  clientId: number,
  surface: CopilotSurface,
  selection: PersistedSourceSelection
): void => {
  persistJson(getLastSourceSelectionStorageKey(clientId, surface), selection)
}

/**
 * Selection to apply when opening a saved chat. Missing key → empty (not last-used):
 * only a prior send writes this slot.
 */
export const resolveSelectionForChat = (
  clientId: number,
  surface: CopilotSurface,
  chatId: string
): PersistedSourceSelection => readChatSourceSelection(clientId, chatId, surface) ?? EMPTY_SOURCE_SELECTION

export const toPersistedKnowledgeSource = (
  source: Pick<KnowledgeCorpusEntityState, 'id' | 'name'>
): PersistedKnowledgeSource => ({
  id: source.id,
  name: source.name
})

export const toPersistedDatasourceEntity = (
  entity: Pick<FlowDatasourceEntity, 'id' | 'type' | 'name'>
): PersistedDatasourceEntity => ({
  id: entity.id,
  type: entity.type,
  name: entity.name
})

/** Placeholder entity for agent.state until live corpora rebuild the full CorpusResponse. */
export const toKnowledgeCorpusStub = (source: PersistedKnowledgeSource): KnowledgeCorpusEntityState => {
  // `createdAt: ''` marks a stub so reconcile can tell it apart from a live corpus.
  const data: CorpusResponse = {
    id: source.id,
    name: source.name,
    description: null,
    createdBy: null,
    owner: null,
    collaborators: [],
    corpusType: 'client',
    vertexCorpusName: null,
    vertexCorpusId: null,
    clientExternalId: null,
    sourceConfigs: [],
    availableToInternal: false,
    availableToExternal: false,
    syncStatus: 'pending',
    lastSyncAt: null,
    lastSyncError: null,
    tags: [],
    documentCount: 0,
    failedDocumentCount: 0,
    createdAt: '',
    modifiedAt: null
  }
  const stub: KnowledgeCorpusEntityState = {
    id: source.id,
    type: 'knowledge_corpus',
    name: source.name,
    data
  }
  return stub
}

/** Placeholder Flow entity until live account options rebuild `data`. */
export const toFlowDatasourceEntityStub = (entity: PersistedDatasourceEntity): FlowDatasourceEntity => ({
  id: entity.id,
  type: entity.type,
  name: entity.name,
  data: {}
})

export const buildPersistedSourceSelection = ({
  optionIds,
  entities,
  cirrusSource,
  knowledgeSources
}: {
  optionIds: string[]
  entities: ReadonlyArray<Pick<FlowDatasourceEntity, 'id' | 'type' | 'name'>>
  cirrusSource: CirrusSourceOption | null
  knowledgeSources: ReadonlyArray<Pick<KnowledgeCorpusEntityState, 'id' | 'name'>>
}): PersistedSourceSelection => {
  let datasource: PersistedDatasourceSelection = { kind: 'none' }
  if (cirrusSource != null) {
    datasource = { kind: 'cirrus', option: cirrusSource }
  } else if (entities.length > 0 && optionIds.length > 0) {
    datasource = {
      kind: 'entities',
      optionIds,
      entities: entities.map(toPersistedDatasourceEntity)
    }
  }

  const slimKnowledge = knowledgeSources.map(toPersistedKnowledgeSource)
  return {
    v: 1,
    datasource,
    knowledgeSourceIds: slimKnowledge.map((source) => source.id),
    knowledgeSources: slimKnowledge
  }
}
